import { motion, AnimatePresence } from 'framer-motion';
import { useReducer } from 'react';
import { createPortal } from 'react-dom';

import * as styles from '@/styles/components/ModalNotice';

type Modal = {
  isShowHeader?: boolean;
  helperText?: string;
  visible: boolean;
  title: string;
  children: React.ReactNode;
  handleClose: () => void;
  isWidth?: boolean;
  isMinWidth?: boolean;
  alignHeader?: string;
  fontSize?: string;
};

type Body = {
  text?: string;
  children?: React.ReactNode;
};

type Footer = {
  children: React.ReactNode;
  isShowBorder?: boolean;
};

export const useModal = (initialState = false) => {
  const [visible, toggle] = useReducer((visible) => !visible, initialState);

  return [visible, toggle] as const;
};

const Modal = ({
  visible = false,
  title,
  children,
  handleClose,
  isShowHeader = true,
  isWidth = false,
  isMinWidth = false,
  helperText,
  alignHeader = 'center',
  fontSize = '2.5rem',
}: Modal) => {
  if (typeof window === 'undefined') {
    return null;
  } else {
    return createPortal(
      <AnimatePresence>
        {visible && (
          <motion.div
            css={styles.root}
            key='dialog'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}>
            <div css={styles.overlay} onClick={handleClose} data-testid='modal-overlay' />
            <div css={styles.content(isWidth, isMinWidth)}>
              {isShowHeader ? (
                <div css={styles.header(alignHeader)}>
                  <div css={styles.title(fontSize)}>
                    {title} {!!helperText && <span css={styles.helperText}>{helperText}</span>}
                  </div>
                </div>
              ) : (
                <div></div>
              )}

              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>,
      document.body,
    );
  }
};

export const Body = ({ text, children }: Body) => {
  return (
    <div css={styles.body}>
      {text && <div css={styles.text}>{text}</div>}
      {children && <div>{children}</div>}
    </div>
  );
};

export const Footer = ({ children }: Footer) => {
  return <div css={styles.footer}>{children}</div>;
};

Modal.Body = Body;
Modal.Footer = Footer;

export default Modal;
